import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Copy, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_MAP = {
  ok: { label: "OK", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  observe: { label: "Observar", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle },
  block: { label: "Bloqueia", cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_MAP[status] || { label: status, cls: "bg-slate-50 text-slate-700 border-slate-200", icon: AlertTriangle };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${cfg.cls}`} data-testid={`prelaunch-status-${status}`}>
      <Icon className="h-3 w-3" strokeWidth={2} />
      {cfg.label}
    </span>
  );
};

const SummaryRow = ({ k, v }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/40 last:border-0" data-testid={`prelaunch-summary-${k}`}>
    <span className="text-sm text-muted-foreground">{k}</span>
    <StatusBadge status={v} />
  </div>
);

export default function PreLaunchCheck() {
  const { apiCall } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ranAt, setRanAt] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall("get", "/admin/pre-launch-check");
      setData(res.data);
      setRanAt(new Date());
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Erro ao executar pre-launch-check";
      setError(detail);
      if (err.response?.status === 403) toast.error("Apenas master pode executar o pre-launch-check.");
      else toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { run(); }, [run]);

  const copyJson = () => {
    if (!data) return;
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success("Relatório JSON copiado");
  };

  const summary = data?.summary || {};
  const sections = data?.sections || {};
  const anyBlock = Object.values(summary).some((v) => v === "block");
  const anyObserve = Object.values(summary).some((v) => v === "observe");
  const overallStatus = anyBlock ? "block" : anyObserve ? "observe" : (data ? "ok" : null);

  return (
    <div className="space-y-6" data-testid="prelaunch-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-serif flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-primary" strokeWidth={1.5} />
            Pré-lançamento Z-API
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auditoria read-only do salão antes de habilitar envios reais. Master-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ranAt && (
            <span className="text-[11px] text-muted-foreground" data-testid="prelaunch-ran-at">
              Executado {ranAt.toLocaleString("pt-BR")}
            </span>
          )}
          {data && (
            <Button variant="outline" size="sm" onClick={copyJson} data-testid="prelaunch-copy-json">
              <Copy className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Copiar JSON
            </Button>
          )}
          <Button onClick={run} disabled={loading} data-testid="prelaunch-run-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {loading ? "Analisando..." : "Rodar novamente"}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="pt-4 text-sm text-red-700 flex items-center gap-2" data-testid="prelaunch-error">
            <XCircle className="h-4 w-4" strokeWidth={1.5} /> {error}
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <Card className={`border-2 ${overallStatus === "block" ? "border-red-300 bg-red-50/40" : overallStatus === "observe" ? "border-amber-300 bg-amber-50/40" : "border-emerald-300 bg-emerald-50/40"}`}>
            <CardContent className="pt-6" data-testid="prelaunch-overall">
              <div className="flex items-center gap-3">
                {overallStatus === "block" ? <XCircle className="h-8 w-8 text-red-600" strokeWidth={1.5} /> : overallStatus === "observe" ? <AlertTriangle className="h-8 w-8 text-amber-600" strokeWidth={1.5} /> : <CheckCircle2 className="h-8 w-8 text-emerald-600" strokeWidth={1.5} />}
                <div>
                  <div className="text-lg font-semibold">
                    {overallStatus === "block" && "Pré-lançamento com BLOQUEIOS — resolva antes de habilitar WHATSAPP_LIVE_ENABLED=1"}
                    {overallStatus === "observe" && "Pré-lançamento com PONTOS DE OBSERVAÇÃO — pode habilitar após revisar"}
                    {overallStatus === "ok" && "Pré-lançamento OK — seguro habilitar WHATSAPP_LIVE_ENABLED=1"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    live_sends_disabled = <code>{summary.live_sends_disabled || "?"}</code> · Se estiver <code>observe</code> significa que WHATSAPP_LIVE_ENABLED já está =1.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Resumo</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {Object.entries(summary).map(([k, v]) => <SummaryRow key={k} k={k} v={v} />)}
            </CardContent>
          </Card>

          {/* Unidades */}
          {sections.units && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2">Unidades <Badge variant="outline">total: {sections.units.total}</Badge></CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Ativas" value={sections.units.active_total} />
                  <Stat label="Ativas c/ Z-API" value={sections.units.active_with_zapi_instance_id} />
                  <Stat label="Ativas sem Z-API" value={(sections.units.active_missing_sample || []).length} intent={((sections.units.active_missing_sample || []).length > 0) ? "warn" : "ok"} />
                  <Stat label="Congeladas" value={sections.units.inactive_total} />
                </div>
                {(sections.units.active_missing_sample || []).length > 0 && (
                  <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-3" data-testid="prelaunch-units-missing">
                    <strong>Ativas sem zapi_instance_id (bloqueiam envio real):</strong>
                    <ul className="mt-1 list-disc list-inside">
                      {(sections.units.active_missing_sample || []).map((u) => (
                        <li key={u.unit_id}><code>{u.unit_id}</code> — {u.name}</li>
                      ))}
                    </ul>
                    <div className="mt-2">Vá em <a className="underline" href="/configuracoes">Configurações → Unidades</a> → botão <em>Configurar Z-API</em>.</div>
                  </div>
                )}
                {(sections.units.duplicate_instance_ids || []).length > 0 && (
                  <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-3" data-testid="prelaunch-units-dup">
                    <strong>zapi_instance_id duplicado entre unidades:</strong>
                    <pre className="mt-1 text-[11px] overflow-auto">{JSON.stringify(sections.units.duplicate_instance_ids, null, 2)}</pre>
                  </div>
                )}
                {(sections.units.inactive_units || []).length > 0 && (
                  <div className="text-xs bg-slate-50 border border-slate-200 text-slate-700 rounded p-3" data-testid="prelaunch-units-inactive">
                    <strong>Congeladas (não participam de nada):</strong>
                    <ul className="mt-1 list-disc list-inside">
                      {(sections.units.inactive_units || []).map((u) => (
                        <li key={u.unit_id}><code>{u.unit_id}</code> — {u.name} {u.deactivated_at && `(desde ${u.deactivated_at.slice(0, 10)})`}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Automations */}
          {sections.automations && (
            <Card>
              <CardHeader><CardTitle className="text-base">Automações qualificadas (cron/time_based)</CardTitle></CardHeader>
              <CardContent>
                <div className="text-xs text-muted-foreground mb-2">
                  Se WHATSAPP_LIVE_ENABLED=1, estas automações DISPARAM sozinhas. Certifique-se de que estão corretas antes de habilitar.
                </div>
                {(sections.automations.list || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">Nenhuma automação com gatilho automático ativo.</div>
                ) : (
                  <ul className="space-y-2">
                    {(sections.automations.list || []).map((a) => (
                      <li key={a.automation_id} className="p-2 border rounded flex items-center gap-2 text-sm" data-testid={`prelaunch-auto-${a.automation_id}`}>
                        <span className="font-medium">{a.name}</span>
                        <Badge variant="outline">{a?.trigger?.type}</Badge>
                        <Badge variant="outline">{a.channel}</Badge>
                        <span className="ml-auto text-xs text-muted-foreground">next: {a.next_due_at || "-"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}

          {/* Locks & Sends */}
          <div className="grid md:grid-cols-2 gap-4">
            {sections.locks && (
              <Card>
                <CardHeader><CardTitle className="text-base">Dispatch Locks</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    <Stat label="Total" value={sections.locks.total} />
                    <Stat label="Ativos" value={sections.locks.active} intent={sections.locks.active > 0 ? "warn" : "ok"} />
                    <Stat label="Vencidos" value={sections.locks.expired} />
                  </div>
                </CardContent>
              </Card>
            )}
            {sections.sends && (
              <Card>
                <CardHeader><CardTitle className="text-base">Campaign Sends</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(sections.sends.by_status || {}).map(([k, v]) => (
                      <Stat key={k} label={k} value={v} intent={k === "sending" || k === "queued" ? "warn" : "ok"} />
                    ))}
                    <Stat label="Presos > 1h" value={sections.sends.stuck_over_1h || 0} intent={(sections.sends.stuck_over_1h || 0) > 0 ? "warn" : "ok"} />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Env */}
          {sections.env && (
            <Card>
              <CardHeader><CardTitle className="text-base">Variáveis de ambiente efetivas</CardTitle></CardHeader>
              <CardContent>
                <pre className="text-[11px] bg-muted/50 p-3 rounded overflow-auto" data-testid="prelaunch-env">{JSON.stringify(sections.env, null, 2)}</pre>
              </CardContent>
            </Card>
          )}

          {/* Raw */}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-2">Ver JSON completo</summary>
            <pre className="bg-muted/50 p-3 rounded overflow-auto" data-testid="prelaunch-raw">{JSON.stringify(data, null, 2)}</pre>
          </details>

          <div className="text-xs text-muted-foreground">
            Endpoint: <code>GET /api/admin/pre-launch-check</code> · <a href="https://app.z-api.io" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">painel Z-API <ExternalLink className="h-3 w-3" /></a>
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, intent }) => {
  const cls = intent === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" : intent === "err" ? "bg-red-50 border-red-200 text-red-800" : "bg-muted/40 border-border";
  return (
    <div className={`p-3 rounded border ${cls}`} data-testid={`prelaunch-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold">{value ?? 0}</div>
    </div>
  );
};
